/**
 * archiveFileSearchContainer.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2023 Secured Communications. All rights reserved.
 *
**/

import { DateTime } from "luxon";
import * as React from "react";

import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";

import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DateTimeValidationError } from "@mui/x-date-pickers/models";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Autocomplete, Button, CircularProgress, IconButton, TextField } from "@mui/material"

import { AuthContext } from "../auth/auth.context";

import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { FileDownloader } from "../components/fileDownloader.component";

import { StoredFile, sizeDisplay } from "../models/file";

import { archiveServiceFactory, FileSearchTerms } from "../services/archive.service";
import { authServiceFactory } from "../services/auth.service";

type SearchKey = "key" | "userID" | "fileID";
interface SearchOption {
  key: SearchKey,
  value: string
}

interface Props {
  pemPrivateKey: string
}
export function ArchiveFileSearchContainer(props: Props): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const [searching, setSearching] = React.useState<boolean>(false);
  const [searchOptions, setSearchOptions] = React.useState<Array<SearchOption>>([]);
  const [selectedTerms, setSelectedTerms] = React.useState<Array<SearchOption>>([]);
  const [storedfiles, setStoredFiles] = React.useState<Array<StoredFile>>([]);
  const [dlLink, setDlLink] = React.useState<string>("");
  const [errMsg, setErrMsg] = React.useState<string>("");
  const [startDate, setStartDate] = React.useState<DateTime>(DateTime.now().startOf("day").minus({months: 6}));
  const [endDate, setEndDate] = React.useState<DateTime>(DateTime.now().endOf("day"));
  const [startDateErr, setStartDateErr] = React.useState<string>("");
  const [endDateErr, setEndDateErr] = React.useState<string>("");

  function onBlur(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (propName === "startDate") {
      if (!startDate || startDate.invalidReason) {
        setStartDateErr("Please, set a starting date/time");
      } else if (endDate && endDate < startDate) {
        setStartDateErr("Starting date/time is after the ending date/time");
      } else {
        setStartDateErr("");
      }
    } else if (propName === "endDate") {
      if (!endDate || endDate.invalidReason) {
        setEndDateErr("Please, set an ending date/time");
      } else if (startDate && endDate < startDate) {
        setEndDateErr("Ending date/time is before the starting date/time");
      } else {
        setEndDateErr("");
      }
    }
  }
  function onDateError(error: DateTimeValidationError | null, propName: string) {
    if (propName === "startDate") {
      switch (error) {
        case "maxDate":
          setStartDateErr("Starting date/time must be in the past");
          break;
        case null:
          setStartDateErr("");
          break;
        default:
          setStartDateErr("Please, set a starting date/time");
          break;
      }
    } else if (propName === "endDate") {
      switch (error) {
        case "maxDate":
          setEndDateErr("Ending date/time must be in the past");
          break;
        case null:
          setEndDateErr("");
          break;
        default:
          setEndDateErr("Please, set an ending date/time");
          break;
      }
    }
  }

  function makeSearchTerms(): FileSearchTerms {
    const terms: FileSearchTerms = {};
    for (const term of selectedTerms) {
      switch (term.key) {
        case "userID":
          if (!terms.userIDs) { terms.userIDs = []; }
          terms.userIDs.push(term.value);
          break;
        case "fileID":
          if (!terms.fileIDs) { terms.fileIDs = []; }
          terms.fileIDs.push(term.value);
          break;
        case "key":
          if (!terms.terms) { terms.terms = []; }
          terms.terms.push(term.value);
          break;
      }
    }
    if (startDate) {
      terms.startDate = startDate.toMillis();
    }
    if (endDate) {
      terms.endDate = endDate.toMillis();
    }
    return terms;
  }

  async function search() {
    if (!authContext.membership) { return; }
    if (searching) { return; }
    try {
      const terms: FileSearchTerms = makeSearchTerms();
      setSearching(true);
      const results = await archiveServiceFactory().searchFiles(
        authContext.membership.accountID, terms);
      setStoredFiles(results);
    } catch (err) {
      console.error("Error searching:", err);
      setErrMsg((err as Error).message);
    } finally {
      setSearching(false);
    }
  }

  async function download(file: StoredFile) {
    if (!authContext.membership) { return; }
    try {
      const token = authServiceFactory().accessToken;
      const archiveService = archiveServiceFactory();
      const key = await archiveService.getDownloadKey(authContext.membership.accountID,
        file.groupID, file._id, file.keyID, props.pemPrivateKey);
      const link = await archiveService.makeFileDownloadLink(authContext.membership.accountID,
        file.groupID, file._id, key, token);
      setDlLink(link);
    } catch (err) {
      console.error("Error downloading:", err);
      setErrMsg((err as Error).message);
    }
  }

  function nameForKey(key: SearchKey): string {
    switch (key) {
      case "userID": return "User ID:";
      case "fileID": return "File ID";
      case "key": return "Keyword:";
    }
  }
  function makeOptionLabel(term: SearchOption | string): string {
    if (!term) { return ""; }
    if (typeof term === "string") {
      return `${nameForKey("key")} ${term}`;
    }
    return `${nameForKey(term.key)} ${term.value}`;
  }
  function selectedSearchTerm(_event: React.SyntheticEvent, terms: ReadonlyArray<SearchOption | string>) {
    console.log("selected:", terms);
    setSelectedTerms(terms.filter(t => t && !(typeof t === "string")) as Array<SearchOption>);
  }
  function updateSearchTerms(_event: React.SyntheticEvent, newInputValue: string) {
    const options: Array<SearchOption> = [{key: "key", value:  newInputValue}];
    if (/^[\da-fA-F]{1,24}$/.test(newInputValue)) {
      options.push({key: "userID", value: newInputValue});
    }
    setSearchOptions(options);
  }

  /*
   renderInput={(props: TextFieldProps) =>
              <TextField
                size="small"
                name="startDate"
                color="secondary"
                type="datetime-local"
                error={!!startDateErr}
                helperText={startDateErr}
                onBlur={onBlur}
                {...props}
              />}
    renderInput={(props: TextFieldProps) =>
              <TextField
                size="small"
                name="endDate"
                color="secondary"
                type="datetime-local"
                error={!!endDateErr}
                helperText={endDateErr}
                onBlur={onBlur}
                {...props}
              />}
    inputFormat="LL/dd/yyyy h:mm a"
  */
  return <div className="archive-search-container file-search">
    <h2>File Search</h2>
    <form className="form-wrapper">
      <div className="left-justified-row">
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DateTimePicker className="start-date-input"
            label="Start Date/Time"
            name="startDate"
            disableFuture
            value={startDate}
            onError={e => onDateError(e, "startDate")}
            onChange={(newValue: DateTime | null) => {
              newValue && setStartDate(newValue);
            }}
            slotProps={{
              textField: {
                size: "small",
                helperText: startDateErr,
                error: !!startDateErr,
                onBlur: onBlur
              }
            }}
          />
          <DateTimePicker className="end-date-input"
            label="End Date/Time"
            name="endDate"
            value={endDate}
            onError={e => onDateError(e, "endDate")}
            onChange={(newValue: DateTime | null) => {
              newValue && setEndDate(newValue);
            }}
            slotProps={{
              textField: {
                size: "small",
                helperText: endDateErr,
                error: !!endDateErr,
                onBlur: onBlur
              }
            }}
          />
        </LocalizationProvider>
        <Autocomplete fullWidth
          multiple
          freeSolo
          size="small"
          options={searchOptions}
          getOptionLabel={makeOptionLabel}
          onChange={selectedSearchTerm}
          onInputChange={updateSearchTerms}
          renderInput={(params) =>
            <TextField variant="outlined" color="secondary" {...params} placeholder="Search:" />} />
        <Button variant="contained" onClick={search}>
          {searching ? <CircularProgress color="info" size={21} /> : "Search"}
        </Button>
        <FileDownloader src={dlLink} didTrigger={() => setDlLink("") } />
      </div>
    </form>
    <table className="file-search-table">
      <tbody>
        <tr>
          <th className="action-col"></th>
          <th className="time-col">Uploaded</th>
          <th className="groupID-col">Group</th>
          <th className="size-col">Size</th>
          <th className="filename-col">File Name</th>
        </tr>
        {storedfiles.map(storedfile => <tr key={storedfile._id}>
          <td className="action-col">
            <div className="actions">
              <IconButton size="small" aria-label="download" onClick={() => download(storedfile)}>
                <i className="fa-solid fa-download" />
              </IconButton>
            </div>
          </td>
          <td className="time-col">{storedfile.created?.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS) || ""}</td>
          <td className="groupID-col">{storedfile.groupID}</td>
          <td className="size-col">{sizeDisplay(storedfile.filesize)}</td>
          <td className="filename-col">{storedfile.filename}</td>
        </tr>)}
      </tbody>
    </table>
    <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("") } />
  </div>;
}
